import { QueryFunctionContext } from 'react-query'
import request from 'services/axios'

export const settingImageChange = async (payload: ISettingImagePayload) => {
    const { data } = await request.post(
        `/moneypool_management/moneypool/${payload.id}/update/`,

        {
            name: payload.name,
            bank_balance: payload.bankBalance,
            interval: payload.interval,
            share_value: payload.shareValue,
            day_of_duetion: payload.dayOfDuetion,
            type: payload.type,
            notification_announce: payload.notificationAnnounce,
            sms_announce: payload.smsAnnounce,
            custom_avatar: 'None',
            image_id: payload.imageId,
        }
    )
    return data
}

export const settingManageChange = async (payload: ISettingManagePayload) => {
    const { data } = await request.post(
        `/moneypool_management/moneypool/${payload.id}/update/`,

        {
            name: payload.name,
            bank_balance: payload.bankBalance,
            interval: payload.interval,
            share_value: payload.shareValue,
            day_of_duetion: payload.dayOfDuetion,
            type: payload.type,
            notification_announce: payload.notificationAnnounce,
            sms_announce: payload.smsAnnounce,
            custom_avatar: 'None',
            image_id: payload.imageId,
            bank_account: payload.bankAccount,
            trust_state: payload.trustState,
        }
    )
    return data
}

export const settingChangeShareUser = async (
    payload: IChangeShareSettingPayload
) => {
    const { data } = await request.post(
        `/moneypool_management/${payload.moneyPool}/poolship/update/${payload.poolShip}/`,

        {
            number_of_shares: payload.share,
            role: payload.role,
            ...keepGuarantorAndRecommender(payload),
        }
    )
    return data
}

/**
 * poolship/update/ for an edit that is NOT a share change — role, guarantor.
 *
 * These used to resend the member's `number_of_shares` every time, because
 * the endpoint required it. That resend is not harmless (pwa#135):
 *   * the backend opens a new Share row for every count it is sent, even an
 *     unchanged one, and which row is "latest" decides whether the NEXT real
 *     change rewrites this period's حق عضویت or waits for the next one;
 *   * the count comes from the members list as it was when the page loaded,
 *     so a role edit made after another manager changed the count silently
 *     put the old count back.
 * So the count is left out. The backend accepts that since pwa#135.
 *
 * `currentShare` is only for a backend that predates it: that one answers
 * HTTP 200 with code 2052 «number_of_shares» and changes nothing, and this
 * bundle can meet it if the two deploy out of order. Then — and only then —
 * the request is repeated once the old way. Once the backend is out, 2052
 * never comes back and this branch is dead.
 */
async function updatePoolshipWithoutShares(
    moneyPool: string | undefined,
    poolShip: number | undefined,
    body: Record<string, unknown>,
    currentShare: number | undefined
) {
    const url = `/moneypool_management/${moneyPool}/poolship/update/${poolShip}/`
    const { data } = await request.post(url, body)
    if (
        data?.code === 2052 &&
        data?.params === 'number_of_shares' &&
        currentShare != null
    ) {
        const retry = await request.post(url, {
            ...body,
            number_of_shares: currentShare,
        })
        return retry.data
    }
    return data
}

export const settingChangeToManager = async (
    payload: IChangeManagerSettingPayload
) =>
    updatePoolshipWithoutShares(
        payload.moneyPool,
        payload.poolShip,
        {
            role: payload.role,
            ...keepGuarantorAndRecommender(payload),
        },
        payload.currentShare
    )

// The update endpoint NULLs any guarantor / recommender key it doesn't get, so
// a role or share change used to silently drop both. Resend the current ones.
function keepGuarantorAndRecommender(payload: {
    guarantor?: number | null
    recommender?: number | null
}) {
    return {
        ...(payload.guarantor != null && { guarantor: payload.guarantor }),
        ...(payload.recommender != null && {
            recommender: payload.recommender,
        }),
    }
}

export const settingSetGuarantor = async (
    payload: ISetGuarantorSettingPayload
) =>
    updatePoolshipWithoutShares(
        payload.moneyPool,
        payload.poolShip,
        {
            role: payload.role,
            ...(payload.guarantor && { guarantor: payload.guarantor }),
            ...(payload.recommender && { recommender: payload.recommender }),
        },
        payload.currentShare
    )

export const deactivateSetting = async (payload: {
    id: string | undefined
    poolShip: number | undefined
}) => {
    const { data } = await request.post(
        `/moneypool_management/${payload.id}/poolship/deactivate/${payload.poolShip}/`
    )
    return data
}

export const activateSetting = async (payload: {
    id: string | undefined
    poolShip: number | undefined
}) => {
    const { data } = await request.post(
        `/moneypool_management/${payload.id}/poolship/activate/${payload.poolShip}/`
    )
    return data
}

/**
 * The name and phone number a manager typed for a member (pwa#149).
 *
 * Until a member signs in, their account is nothing but what the manager
 * typed when adding them, yet a typo in it could only be fixed by support.
 * GET says whether the row is still the manager's to correct (`editable`, or
 * the `reason` it is not); POST corrects it.
 *
 * Only the fields that changed are sent. The backend looks at nothing else,
 * so a name correction never rewrites the number.
 *
 * Like the other poolship views this one answers a refusal with HTTP 200 and
 * a non-2050 `code`; the caller has to look at it.
 */
const memberIdentityUrl = (moneyPool: string | undefined, poolShip: number) =>
    `/moneypool_management/${moneyPool}/poolship/member-identity/${poolShip}/`

export const getMemberIdentity = async (
    moneyPool: string | undefined,
    poolShip: number
) => {
    const { data }: { data: IMemberIdentityResponse } = await request.get(
        memberIdentityUrl(moneyPool, poolShip)
    )
    return data
}

export const setMemberIdentity = async (payload: IMemberIdentityPayload) => {
    const { data } = await request.post(
        memberIdentityUrl(payload.moneyPool, payload.poolShip),
        payload.changes
    )
    return data
}

export const getBoxSettingTransparency = async (ctx: QueryFunctionContext) => {
    const { data }: { data: IBoxSettingTransparency } = await request.get(
        `/api/v1/settings/box-settings/moneypool/${ctx.queryKey[1]}/`
    )
    return data
}

export const settingTransparency = async (
    payload: ISettingTransparencyPayload
) => {
    const { data } = await request.post(
        `/api/v1/settings/box-settings/moneypool/${payload.moneyPool}/`,

        {
            show_others_loans: payload.show_others_loans,
            show_others_loan_demands: payload.show_others_loan_demands,
            show_others_transactions: payload.show_others_transactions,
            show_other_members: payload.show_other_members,
            show_others_to_be_paid_portion:
                payload.show_others_to_be_paid_portion,
            show_box_balance: payload.show_box_balance,
            show_others_installments: payload.show_others_installments,
        }
    )
    return data
}

export const settingBanker = async (payload: ISettingBankerPayload) => {
    const { data } = await request.post(
        `/api/v1/settings/box-settings/moneypool/${payload.moneyPool}/`,

        {
            banker_can_view_finance: payload.banker_can_view_finance,
            banker_can_record_cashin: payload.banker_can_record_cashin,
            banker_can_record_cashout: payload.banker_can_record_cashout,
        }
    )
    return data
}

export const settingSupport = async (payload: ISettingSupportPayload) => {
    const { data } = await request.post(
        `/api/v1/settings/box-settings/moneypool/${payload.moneyPool}/`,
        {
            support_phone: payload.support_phone,
            support_bale: payload.support_bale,
            support_eitaa: payload.support_eitaa,
        }
    )
    return data
}

export const settingSMS = async (payload: ISettingSMSPayload) => {
    const { data } = await request.post(
        `/api/v1/settings/box-settings/moneypool/${payload.moneyPool}/`,

        {
            sms_others_define_loan: payload.sms_others_define_loan,
            sms_pay_portion_reminder: payload.sms_pay_portion_reminder,
            sms_pay_installment_reminder: payload.sms_pay_installment_reminder,
            sms_inform_managers_of_cashins:
                payload.sms_inform_managers_of_cashins,
            sms_opening_time: '00:00',
            sms_closing_time: '23:59:00',
            message_others_loan: payload.message_others_loan,
            notif_others_loan: payload.notif_others_loan,
            loan_digest_interval: payload.loan_digest_interval,
        }
    )
    return data
}
